type Success<T> = {
  success: true;
  result: T;
};
type Failure<E extends Error> = {
  success: false;
  error: E;
};
type Result<T, E extends Error> = Success<T> | Failure<E>;

/**
 * Utility functions for handling try/catch operations with a Result type.
 *
 * @example
 * ```ts
 * // Async usage
 * const result = await tryCatchAsync(async () => await fetchData(arg1, arg2));
 *
 * if (result.success) {
 *   // TypeScript knows result.result is the success value
 *   console.log(result.result);
 * } else {
 *   // TypeScript knows result.error is the Error
 *   console.error(result.error);
 * }
 * ```
 */
export const tryCatchAsync = async <T, Err extends Error>(
  fn: () => Promise<T>,
): Promise<Result<T, Err>> => {
  try {
    return { success: true, result: await fn() };
  } catch (error) {
    return { success: false, error: error as Err };
  }
};

/**
 * Utility functions for handling try/catch operations with a Result type.
 *
 * @example
 * ```ts
 * // Sync usage
 * const result = tryCatchSync(() => parseData(arg1, arg2));
 *
 * if (result.success) {
 *   // TypeScript knows result.result is the success value
 *   console.log(result.result);
 * } else {
 *   // TypeScript knows result.error is the Error
 *   console.error(result.error);
 * }
 * ```
 */
export const tryCatchSync = <T, Err extends Error>(
  fn: () => T,
): Result<T, Err> => {
  try {
    return { success: true, result: fn() };
  } catch (error) {
    return { success: false, error: error as Err };
  }
};
